import {
  MouseEvent,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { Box, Card, cx } from 'ui';
import { useFocusWithin } from 'ui/Hooks';

import classes from './FocusableCard.module.css';
import type { FocusableCardHandle } from './types';

// FocusableCard component that properly forwards refs and handles click-to-focus
export const FocusableCard = forwardRef<
  FocusableCardHandle,
  { children: React.ReactNode; style?: React.CSSProperties }
>(({ children, ...props }, forwardedRef) => {
  const { ref: focusWithinRef, focused } = useFocusWithin<HTMLDivElement>();
  const focusProxyRef = useRef<HTMLDivElement>(null);

  // Expose .focus() to parent
  useImperativeHandle(forwardedRef, () => ({
    focus: () => {
      focusProxyRef.current?.focus();
    },
  }));

  const handleClick = useCallback(
    (e: MouseEvent) => {
      // If already focused, don't interfere - the card itself is focusable
      if (focused) {
        return;
      }

      // Only focus if not already focused and clicked on non-focusable element
      const target = e.target as HTMLElement;
      if (
        // NOTE: We can extend this to include more focusable elements if necessary
        target.tagName !== 'INPUT' &&
        target.tagName !== 'BUTTON' &&
        target.getAttribute('tabindex') == null
      ) {
        focusProxyRef.current?.focus();
      }
    },
    [focused]
  );

  // Clone children and pass focused prop
  const childrenWithFocused = useMemo(() => {
    return isValidElement(children)
      ? cloneElement(children, { focused } as Record<string, unknown>)
      : children;
  }, [children, focused]);

  return (
    <Card
      ref={focusWithinRef}
      radius="sm"
      w="100%"
      padding="none"
      shadow="none"
      onClick={handleClick}
      tabIndex={focused ? 0 : -1}
      className={cx(classes.focusableCard, {
        [classes.focusableCardFocused]: focused,
      })}
      {...props}
    >
      {!focused && (
        <Box
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            backgroundColor: 'transparent',
            top: 0,
            left: 0,
            zIndex: 2,
          }}
          aria-hidden="true"
          // tabIndex={-1}
        />
      )}
      {/* Hidden focus proxy */}
      <Box ref={focusProxyRef} tabIndex={0} draggable={false}>
        {childrenWithFocused}
      </Box>
    </Card>
  );
});

FocusableCard.displayName = 'FocusableCard';
